import { Component, Input, OnInit } from '@angular/core';
import { GridOptions } from 'ag-grid-enterprise';
import { ToastrManager } from '@niceltd/cxone-components/toastr';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { CustomNoRowsOverlayComponent } from '../../shared/custom-no-rows-overlay/custom-no-rows-overlay.component';
import { GridApi, GridReadyEvent, RowDataUpdatedEvent, ValueGetterParams } from 'ag-grid-community';
import { CxoneEtlDataRecalculationService } from './cxone-etl-data-recalculation.service';
import { ETLDataRecalculationFormattedObj, ETLDataRecalculationObj, PERMISSION } from './cxone-etl-data-recalculation.model';
import { StatusCellRendererComponent } from './status-cell-renderer/status-cell-renderer.component';
import { EtlCustomCellValueRendererComponent } from '../../shared/etl-custom-cell-value-renderer/etl-custom-cell-value-renderer.component';
import moment from 'moment';
import { IconDetail, IconType } from './status-cell-renderer/status-cell-renderer.model';
import { DialogService } from 'primeng/dynamicdialog';
import { CxoneEtlRecalculationModalComponent } from '../cxone-etl-recalculation-modal/cxone-etl-recalculation-modal.component';
import { CxoneManageMetricsService } from '../../cxone-manage-metrics/cxone-manage-metrics.service';
import { CxoneEtlReportingSetService } from '../../cxone-etl-reporting-set/cxone-etl-reporting-set.service';
import { Permissions } from './../../cxone-manage-metrics/cxone-manage-metrics.service';

@Component({
    selector: 'cv-cxone-etl-data-recalculation',
    templateUrl: './cxone-etl-data-recalculation.component.html',
    styleUrls: ['./cxone-etl-data-recalculation.component.scss'],
    providers: [DialogService]
})
export class CxoneEtlDataRecalculationComponent implements OnInit {
    public recalculationLabel: string;
    public searchPlaceholderText = '';
    public searchInput = '';
    public recalculationGridOptions: GridOptions;
    public isLocalizationSectionLoaded: boolean;
    public showSpinner: boolean;
    public gridApi: GridApi;
    public gridData: ETLDataRecalculationObj[] = [];
    public formattedData: ETLDataRecalculationFormattedObj[] = [];
    public reportingSetPermissions: string[] = [];
    public metricsPermissions: Permissions;
    public reportingSetsView = false;
    public metricsView = false; 
    public reportingSetsWritePermission = false;
    public metricsWritePermission = false; 
    public showRecalculateBtn = false;
    public metricsInProgress = [];
    public reportingSetsInProgress = [];
    public timerMessage = '';
    public timerInterval: NodeJS.Timeout | undefined;
    public refreshTxt: string;
    public secondsTxt: string;

    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private cxoneEtlDataRecalculationService: CxoneEtlDataRecalculationService,
        public manageMetricsService: CxoneManageMetricsService,
        private cxoneEtlReportingSet: CxoneEtlReportingSetService,
        private toastrManager: ToastrManager,
        private _dialogService: DialogService
    ) { }

    async ngOnInit(): Promise<void> {
        this.showSpinner = true;
        const [reportingSetPermissions, metricsPermissions] = await Promise.all([
            this.cxoneEtlReportingSet.getPermissions("CXOneReportingSetDefinitions"),
            this.manageMetricsService.getManageMetricsPermissions()
        ]);
        this.reportingSetPermissions = reportingSetPermissions || [];
        this.metricsPermissions = metricsPermissions;
        this.isLocalizationSectionLoaded = true;
        this.setGridOptions();
        this.toggleElementPermissions();
        await this.loadLocalization();
    }

    public async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection('ETL_Data_Recalculation');
        }
        catch {
            console.log('Failed to load localization for ETL_Data_Recalculation');
        }
        finally {
            this.searchPlaceholderText = this.getLocalizationString("Search", "Search...");
            this.refreshTxt = this.getLocalizationString("Refresh", "Next refresh in");
            this.secondsTxt = this.getLocalizationString("Seconds", "seconds");
        }
    }

    public getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText("ETL_Data_Recalculation", key, defaultValue);
        return baseString;
    }

    public recalculateData() {
        const dialogRef = this._dialogService.open(CxoneEtlRecalculationModalComponent, {
            width: "950px",
            height: "600px",
            styleClass: 'cx-one-ui modal-center',
            data: {
                metricsView: this.metricsWritePermission,
                reportingSetsView: this.reportingSetsWritePermission,
                metricsInProgress: this.metricsInProgress,
                reportingSetsInProgress: this.reportingSetsInProgress
            }
        });
        dialogRef.onClose.subscribe((data) => {
            if (data) {
                this.loadDataRecalculation();
            }
        });
    }

    public toggleElementPermissions() {
        this.reportingSetsWritePermission = this.reportingSetPermissions.includes(PERMISSION.VIEW) && this.reportingSetPermissions.includes(PERMISSION.CREATE);
        this.metricsWritePermission = this.metricsPermissions?.WriteAccess || this.metricsPermissions?.CreateAccess || false;
        this.reportingSetsView = this.reportingSetPermissions.includes(PERMISSION.VIEW);
        this.metricsView = this.metricsPermissions?.ReadAccess || false;
        this.showRecalculateBtn = this.reportingSetsWritePermission || this.metricsWritePermission;
    }

    public setGridOptions() {
        this.recalculationGridOptions = {
            columnDefs: [
                {
                    field: 'definitions',
                    colId: 'definitions',
                    headerName: this.getLocalizationString("Definition", "Definition"),
                    cellRenderer: 'cxoneETLCustomCellRenderer',
                    cellRendererParams: {
                        showTextPopover: true,
                    },
                    width: 400,
                    resizable: true,
                    sortable: true,
                    suppressMovable: true
                },
                {
                    field: 'dateQueued',
                    colId: 'dateQueued',
                    headerName: this.getLocalizationString("In_Queue", "In Queue"),
                    cellRenderer: 'cxoneETLCustomCellRenderer',
                    cellRendererParams: {
                        showTextPopover: true,
                        convertedValue: (data: ETLDataRecalculationObj) => this.formatValue(data.dateQueued),
                    },
                    width: 250,
                    resizable: true,
                    sortable: true,
                    suppressMovable: true
                },
                {
                    field: 'recalcStart',
                    colId: 'recalcStart',
                    headerName: this.getLocalizationString("Duration", "Duration"),
                    cellRenderer: 'cxoneETLCustomCellRenderer',
                    cellRendererParams: {
                        showTextPopover: true,
                        convertedValue: (data: ETLDataRecalculationObj) => {
                            return (
                                (data?.recalcStart ? moment(data?.recalcStart).format('MMM D, YYYY') : '') +
                                ' - ' +
                                (data?.recalcEnd ? moment(data?.recalcEnd).format('MMM D, YYYY') : '')
                            );
                        },
                    },
                    width: 250,
                    resizable: true,
                    sortable: true,
                    suppressMovable: true
                },
                {
                    field: 'completed',
                    colId: 'completed',
                    headerName: this.getLocalizationString("Status", "Status"),
                    cellRenderer: 'cxoneStatusCellRenderer',
                    cellRendererParams: {
                        iconPath: (data: ETLDataRecalculationObj) => this.getIconPath(data),
                    },
                    valueGetter: (params: ValueGetterParams) => {
                        return params.data?.completed ? this.getLocalizationString("Completed", "Completed") :
                            moment(params.data?.currentRecalculationDate).format('MMM D, YYYY h:mm:ss A');
                    },
                    width: 300,
                    resizable: true,
                    sortable: true,
                    suppressMovable: true
                },
                {
                    field: 'lastModifiedDate',
                    colId: 'lastModifiedDate',
                    headerName: this.getLocalizationString("Modified_Date", "Last Modified"),
                    cellRenderer: 'cxoneETLCustomCellRenderer',
                    cellRendererParams: {
                        showTextPopover: true,
                        convertedValue: (data: ETLDataRecalculationObj) => this.formatValue(data.lastModifiedDate),
                    },
                    width: 250,
                    resizable: true,
                    sortable: true,
                    suppressMovable: true
                },
                {
                    field: 'initiatedBy',
                    colId: 'initiatedBy',
                    headerName: this.getLocalizationString("Initiated_By", "Initiated By"),
                    cellRenderer: 'cxoneETLCustomCellRenderer',
                    cellRendererParams: {
                        showTextPopover: true
                    },
                    resizable: true,
                    sortable: true,
                    suppressMovable: true
                },
                {
                    field: 'type',
                    colId: 'type',
                    headerName: this.getLocalizationString("Type", "Type"),
                    cellRenderer: 'cxoneETLCustomCellRenderer',
                    cellRendererParams: {
                        showTextPopover: true
                    },
                    resizable: true,
                    sortable: true,
                    suppressMovable: true
                }
            ],
            components: {
                CustomNoRowsOverlayComponent,
                cxoneETLCustomCellRenderer: EtlCustomCellValueRendererComponent,
                cxoneStatusCellRenderer: StatusCellRendererComponent
            },
            noRowsOverlayComponent: 'CustomNoRowsOverlayComponent',
            noRowsOverlayComponentParams: {
                noRowsOverlayTextFunc: () => this.searchInput !== '' ?
                    this.getLocalizationString("No_Results_Found", "No results found")
                    : this.getLocalizationString("No_Recalculation_Done_Yet", "No recalculation done yet"),
                noRowsOverlayIconPathFunc: () => {
                    const path = this.searchInput !== '' ?
                        '../App_Themes/CXone/icons/no-results.svg#no-results'
                        : '../App_Themes/CXone/icons/empty-state.svg#empty-state';
                    return path;
                }
            },
            context: this,
            rowHeight: 36,
            headerHeight: 44,
            animateRows: true,
            scrollbarWidth: 8,
            icons: {
                groupContracted:
                    `<div class="sortIcons">
                  <svg class="icon-10">
                      <use href="../App_Themes/CXone/icons/arrow-right.svg#arrow-right">
                  </use></svg>
              </div>`,
                groupExpanded:
                    `<div class="sortIcons">
                  <svg class="icon-10">
                      <use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use>
                  </svg>
              </div>`
            },
            onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
            onRowDataUpdated: (event: RowDataUpdatedEvent) => this.setRowCountLabel(),
            getRowId: ({ data: rowData }) => rowData._id
        };
    }

    public formatValue(value: string): string {
        return value ? moment(value).format('MMM D, YYYY h:mm:ss A') : '';
    }

    public getIconPath(data: ETLDataRecalculationObj): IconDetail {
        if (data?.completed) {
            return {
                iconType: IconType.Completed,
            };
        } else {
            return {
                iconType: IconType.InProgress,
            };
        }
    }

    startTimer(): void {
        // Clear the previous timer if it exists
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }

        let countdown = 60;
        this.updateTimerMessage(countdown);
        this.timerInterval = setInterval(() => {
            countdown--;

            if (countdown > 0) {
                this.updateTimerMessage(countdown);
            } else if (countdown === 0) {
                clearInterval(this.timerInterval);
                this.timerMessage = '';
                this.loadDataRecalculation();
            }
        }, 1000);
    }

    updateTimerMessage(countdown): void {
        this.timerMessage = `${this.refreshTxt} ${countdown} ${this.secondsTxt}`;
    }

    public async loadDataRecalculation() {
        try {
            this.formattedData = [];
            this.metricsInProgress = [];
            this.reportingSetsInProgress = [];
            const response = await this.cxoneEtlDataRecalculationService.loadData();
            response.forEach(item => {
                if ((this.metricsView && item.type === 'Metric') || (this.reportingSetsView && item.type === 'Reporting Set')) {
                    const formattedItem = {
                        ...item,
                        dateCompleted: item.completed
                            ? this.getLocalizationString("Completed", "Completed")
                            : moment(item.currentRecalculationDate).format('MMM D, YYYY h:mm:ss A'),
                        recalcStartFormatted: (item.recalcStart ? moment(item.recalcStart).format('MMM D, YYYY') : '') +
                            ' - ' +
                            (item.recalcEnd ? moment(item.recalcEnd).format('MMM D, YYYY') : ''),
                        dateQueuedFormatted: this.formatValue(item.dateQueued),
                        lastModifiedDateFormatted: this.formatValue(item.lastModifiedDate),
                    };

                    this.formattedData.push(formattedItem);

                    // Check for items which are in progress
                    if (!item.completed) {
                        const inProgressArray = item.type === 'Metric' ? this.metricsInProgress : this.reportingSetsInProgress;
                        inProgressArray.push(formattedItem);
                    }
                }
            });
            this.gridData = this.formattedData;
            if (this.searchInput !== '') {
                this.filterGridData(this.searchInput);
            } else {
                this.setGridRecordData();
            }

            if (this.metricsInProgress.length > 0 || this.reportingSetsInProgress.length > 0) {
                this.startTimer();
            }
        } catch (error) {
            this.gridData = [];
            this.setGridRecordData();
            this.toastrManager.showToaster('error', this.getLocalizationString('Internal_Server_Error', 'Internal server error. Try again.'));
        }
    }

    public setGridRecordData() {
        this.gridApi.setRowData([]);
        this.gridApi.setRowData(this.gridData);
        this.showSpinner = false;
        this.manageNoRowsOverlay();
        this.gridApi.sizeColumnsToFit();
    }

    public onGridReady(event: GridReadyEvent) {
        this.gridApi = event.api;
        this.loadDataRecalculation();
    }

    public onSearchCriteriaChange = (searchCriteria: string) => {
        this.searchInput = searchCriteria;
        this.filterGridData(searchCriteria);
        this.manageNoRowsOverlay();
    };

    public manageNoRowsOverlay() {
        if (this.gridApi.getDisplayedRowCount() === 0)
            this.gridApi.showNoRowsOverlay();
        else
            this.gridApi.hideOverlay();
    }

    public setRowCountLabel() {
        let count = 0;
        this.gridApi.forEachNodeAfterFilter(() => {
            count++;
        });
        this.recalculationLabel = this.getTotalRecalculationsText(count);
    }

    public getFilterText(): string {
        return this.searchInput.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    }

    public getTotalRecalculationsText(count: number): string {
        const key: string = count === 1 ? 'Total_Recalculations_Singular' : 'Total_Recalculations_Plural';
        const defaultValue: string = count === 1 ? '{0} recalculation' : '{0} recalculations';
        return this.cvLocalizationService.Format(this.getLocalizationString(key, defaultValue), count);
    }

    public filterGridData(searchQuery: string): void {
        this.gridData = this.formattedData.filter((item) => {
            return (
                item.definitions?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.dateQueuedFormatted?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.recalcStartFormatted?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.currentRecalculationDate?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.dateCompleted?.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.lastModifiedDateFormatted?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.initiatedBy?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.type?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        });
        this.setGridRecordData();
    }
}
